import { capitalize } from "lodash";
import moment from 'moment-timezone';

import config from '../config/config';

export const formattedDietaries = (attendeeInformationResponse = {}) => {
    let result = [];
    const hasDietaryResponse = attendeeInformationResponse?.answer?.some(item => item && 'dietaryPreference' in item);
    if (hasDietaryResponse) {
        const preferenceLabels = {
            VEGAN: "Vegan",
            VEGETARIAN: "Vegetarian",
            GLUTEN_FREE: "Gluten Free",
        };
        const counts = {};

        for (const item of attendeeInformationResponse?.answer) {
            const { dietaryPreference, type } = item;
            if (dietaryPreference) {
                const key = `${dietaryPreference}_${type}`;
                counts[key] = (counts[key] || 0) + 1;
            }
        }

        result = Object.entries(counts).map(([key, count]) => {
            const lastUnderscore = key.lastIndexOf('_');
            const diet = key.substring(0, lastUnderscore);
            const type = key.substring(lastUnderscore + 1);
            const label = preferenceLabels[diet] || capitalize(diet.toLowerCase().replace(/_/g, ' '));
            return `${label} X ${count} (${type})`;
        });
    }
    return result;
}

// Maps an occasion booking document (a Booking with isOccasion + embedded
// component bookings) into the wrapper shape the <Occasion> component consumes.
// The whole document is carried through: the occasion is itself a booking, so its
// notification / deliveryStatus / hq / product fields drive the header's send
// confirmation and send gameplay actions.
export const toOccasionWrapper = (doc) => ({
    ...doc,
    customer: {
        name: doc.customer?.name || doc.hq?.customer?.name || '',
        phone: doc.customer?.phone || doc.hq?.customer?.phone?.number || '',
    },
    bookings: doc.bookings || [],
});

/**
 * The HQ event a booking belongs to. `HQ-AEX-<id>` is the reference operators quote and search
 * on; the Mongo `_id` is not usable anywhere outside aex.
 *
 * A component booking of an occasion carries its parent's `hqId` and `hq` verbatim
 * (ac-experience `booking/component-booking.ts` buildComponentBookings), so this returns the
 * same reference for an occasion and for every one of its components — which is why the planner
 * prints it once, on the occasion header.
 */
export const getHqEventId = (booking = {}) => booking.hq?.id || booking.hqId || '';

export const getHqEventReference = (booking) => `HQ-AEX-${getHqEventId(booking)}`;

export const getHqEventUrl = (booking) => `${config.hqURL}/events/${getHqEventId(booking)}`;

/**
 * The event's date in the timezone the experience happens in, with the `(AEST)` abbreviation and
 * the ` - 2:00pm` suffix a booking with a time gets. `dateText` and `timeText` are returned apart
 * because the planner row renders them with a space between.
 *
 * Lifted verbatim out of `planners/BookingSummary.jsx`, quirks included: `zoneAbbr()` is read at
 * *now* in that zone rather than at the event date, and a booking with no `hq.dateTime` reads as
 * today, because `moment.utc(undefined)` is a valid moment. The row's `'No Date'` branch was
 * therefore never reachable.
 */
export const getBookingEventDate = (booking = {}) => {
    const hq = booking.hq || {};
    const timeZoneId =
        hq.timezone || booking.product?.wheres?.[0]?.timeZoneId || hq.location?.timeZoneId || 'UTC';
    const eventDate = moment.utc(hq.dateTime).tz(timeZoneId);

    return {
        timeZoneId,
        eventDate,
        dateText: `${eventDate.format('ddd DD MMM YYYY')} (${moment.tz(timeZoneId).zoneAbbr()})`,
        timeText: hq.hasTime ? ` - ${eventDate.format('h:mma')}` : '',
    };
};

/**
 * The two generated stop types a component booking can carry, inlined rather than imported
 * from `components/common/routes` to keep `utils/` from depending on `components/`.
 *
 * `component-stop` is seeded at HQ sync for a partner component, or one delivered by a single
 * provider item, so a planner has a cost row from the start - and where the component names a
 * provider item, that stop carries the real item and its price. It is where a single-stop
 * component's ticket goes.
 *
 * The `ticket-only` stop it used to sit beside is gone: that was the route-replacing ticket
 * path, whose stop had no itemId and so a cost nobody could ever edit. A ticket hangs off a
 * provider's item now, in a real route or in this automatic one.
 */
const COMPONENT_STOP = 'component-stop';

/**
 * The stop a provider cost has already been recorded against: the one-stop route a partner or
 * provider-item component is seeded with at HQ sync. Also what says "this route is generated,
 * there is no gameplay behind it", which is why the planner's Preview shows the tickets
 * themselves rather than launching eee.
 */
export const getCostBearingStop = (booking = {}) => {
    const stop = booking.route?.stops?.[0];
    return stop?.type === COMPONENT_STOP ? stop : null;
};

/**
 * Every stop of a booking's route carrying a ticket, in route order.
 *
 * Route order, not stop type: a ticket belongs to a stop, so a component can be a multi-stop
 * experience where only some stops are ticketed — a lunch reservation and a balloon ride, but
 * nothing on dessert — and a component that is a single stop by nature is ticketed through
 * stop 0 of the one-stop route it is seeded with.
 *
 * The bar is a file *or* a booked time, matching what ac-experience's
 * `toStopTicketsWithSignedUrls` keeps: a time recorded before the file arrives is still
 * something the customer is shown.
 */
const getTicketedStops = (booking = {}) =>
    (booking.route?.stops ?? []).filter(
        (stop) => stop?.ticket?.documents?.length || stop?.ticket?.time,
    );

/**
 * True once a component has something the customer will actually receive. One ticket is
 * enough — the presence of any is what counts as complete, so gameplay becomes sendable
 * exactly when there is a ticket to send.
 */
export const hasTicketFulfilment = (booking = {}) => getTicketedStops(booking).length > 0;

/**
 * True once a ticket *file* is attached anywhere on the route. Stricter than
 * `hasTicketFulfilment`, which counts a booked time on its own: the planner's "not attached"
 * warning is about the file, since a time is an intention and the file is the ticket.
 */
export const hasTicketDocuments = (booking = {}) =>
    (booking.route?.stops ?? []).some((stop) => stop?.ticket?.documents?.length);
